/**
 * FilterModal Component
 *
 * Handles:
 * - Sorting (A→Z, Z→A, oldest→latest, etc.)
 * - Search inside filter values
 * - Apply / Clear column filter
 * - Persisting filter selections to localStorage
 * - Date range filtering for date-based columns
 * - Infinite scroll for long filter item lists
 * - Click-outside-to-close behavior
 */
import { FunctionComponent, useEffect, useState, useRef, useCallback } from 'react';
import styles from './FilterModal.module.css';
import { AiOutlineArrowUp } from 'react-icons/ai';
import { AiOutlineArrowDown } from 'react-icons/ai';
import { AiOutlineSearch, AiOutlineClose } from 'react-icons/ai';
import { Checkbox } from '@material-ui/core';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroll-component';
import { dateFieldList } from '../../../Values/tables';
import moment from 'moment';
import { setClearFilter, setFilter } from '../../../Redux/actions';
import { themeColors } from '../../theme';

type props = {
  expand: boolean;
  column: any;
  filterObject: { [key: string]: string[] };
  hide: () => void;
  filterId: string;
  globalClearFilter: boolean;
  filter: any;
  theme: any;
};

const FilterModal: FunctionComponent<props> = ({
  column,
  filterObject,
  expand,
  hide,
  filterId,
  globalClearFilter,
  filter,
  theme
}) => {

  /** -------------------------------
   * refs & states
   * --------------------------------
   */
  const dispatcher = useDispatch();
  const filterRef = useRef(null);
  const [filterRow, setFilterRow] = useState<any[]>([]);
  const [dateArray, setDateArray] = useState<Date[]>([]);
  const [sort, setSort] = useState(0);
  const [searchedItem, setsearchedItem] = useState('');
  const [filterItems, setFilterItems] = useState<string[]>([]);
  const [currentLimit, setCurrentLimit] = useState(5);

  /** -------------------------------------------
 * Clears filter + sort + stored selections
 * ------------------------------------------- */
  const clearFilter = () => {
    column.clearSortBy();
    setSort(0);
    setFilterRow([]);
    setDateArray([]);
    localStorage.setItem(filterId, JSON.stringify([]));
    localStorage.setItem(filterId + 'sort', '0');
    column.setFilter(undefined);
    hide();
  };

  /** Hides modal & resets infinite scroll */
  const close = () => {
    hide();
    setCurrentLimit(5);
  };

  /** -------------------------------------------
  * Auto-clear filters when globalClearFilter fires
  * ------------------------------------------- */
  useEffect(() => {
    if (globalClearFilter === true) {
      clearFilter();
      dispatcher(setClearFilter(false));
    }
  }, [globalClearFilter]);

  /** -------------------------------------------
 * Load previously stored filters & sorting
 * ------------------------------------------- */
  useEffect(() => {
    const savedFilters = localStorage.getItem(filterId);
    if (savedFilters) {
      if (dateFieldList.includes(column.Header)) {
        let dates = JSON.parse(savedFilters);
        if (dates?.length === 2) {
          let newdates = [moment(dates[0]).toDate(), moment(dates[1]).toDate()];

          setDateArray(newdates);
          setFilterRow(newdates);
        }
      } else {
        setFilterRow(JSON.parse(savedFilters));
      }
    }
    const savedSort = localStorage.getItem(filterId + 'sort');

    if (savedSort) {
      setTimeout(() => {
        if (savedSort === '1') {
          setSort(1);
        } else if (savedSort === '2') {
          setSort(2);
        } else {
          setSort(0);
        }
      }, 100);
    } else {
      setTimeout(() => {
        setSort(0);
      }, 100);
    }
    // eslint-disable-next-line
  }, []);

  /** Reset internal sort if table reset externally */
  useEffect(() => {
    const savedSort = localStorage.getItem(filterId + 'sort');
    if (column.isSorted === false && savedSort === null) setSort(0);
    // eslint-disable-next-line
  }, [column.isSorted]);

  /** Apply sorting whenever filterObject updates */
  useEffect(() => {
    if (sort === 1) {
      column.toggleSortBy(false);
    } else if (sort === 2) {
      column.toggleSortBy(true);
    }
    // eslint-disable-next-line
  }, [filterObject]);

  /** Apply sorting when sort state changes */
  useEffect(() => {
    if (sort === 1) {
      column.toggleSortBy(false);
    } else if (sort === 2) {
      column.toggleSortBy(true);
    }
    // eslint-disable-next-line
  }, [sort]);

  /** Sync filterRow → react-table column filter */
  useEffect(() => {
    if (filterRow.length === 0) {
      column.setFilter([]);
    } else {
      column.setFilter(filterRow);
    }
  }, [filterRow, column, filterObject]);

  /** Populate filterItems from table's filterObject */
  useEffect(() => {
    if (filterObject[column.id]) {
      setFilterItems(filterObject[column.id]);
    }
  }, [column.id, filterObject]);

  /** Search filter */
  useEffect(() => {
    if (searchedItem.length === 0) {
      setFilterItems(filterObject[column.id]);
    } else {
      const data = filterItems.filter((item) =>
        item.toLowerCase().includes(searchedItem.toLowerCase())
      );
      setFilterItems(data);
    }
    // eslint-disable-next-line
  }, [searchedItem]);

  /** -------------------------------------------
    * Detect clicks outside modal → close it
    * ------------------------------------------- */
  const handleClickOutside = useCallback((event: any) => {
    const element: any = filterRef.current;
    if (element && !element.contains(event.target)) {
      hide();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (expand === true) {
      setTimeout(
        () => document.addEventListener('click', handleClickOutside),
        100
      );
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line
  }, [expand]);

  /** -------------------------------------------
   * Save date selection (From / To)
   * ------------------------------------------- */
  const setDateFilter = (date: Date | null, type: 'from' | 'to') => {
    if (date === null) {
      return;
    }
    let tempDateArray = [...dateArray];
    if (type === 'from') {
      const newDate = moment(date).startOf('day').toDate();
      tempDateArray[0] = newDate;
    } else {
      const newDate = moment(date).endOf('day').toDate();
      tempDateArray[1] = newDate;
    }

    setDateArray(tempDateArray);
    if (tempDateArray[0] && tempDateArray[1]) {
      setFilterRow(tempDateArray);
      localStorage.setItem(filterId, JSON.stringify(tempDateArray));
    }
  };

  /** Dynamic theme support */
  const containerStyles = { backgroundColor: theme ? themeColors.dropdownBackgroundColor : themeColors.dropdownBackgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }
  const checkboxStyles = { color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }

  /** -------------------------------------------
  * Render
  * ------------------------------------------- */
  const filterClass = classNames(styles.parent, {
    [styles.showFilterAnim]: expand === true,
  });

  return (
    <div className={filterClass} ref={filterRef} style={containerStyles}>

      {/* SORT ASC */}
      <div
        className={styles.child}
        style={containerStyles}
        onClick={() => {
          setSort(1);
          hide();
          localStorage.setItem(filterId + 'sort', '1');
        }}
      >
        <AiOutlineArrowUp color={sort === 1 ? '#272944' : '#000'} size={20} />
        <p className={styles.text}>
          {column.Header === 'Lead Assign Date & Time'
            ? 'Oldest to latest'
            : column.Header === 'Created Date & Time'
              ? 'Oldest to latest'
              : 'Sort A to Z'}
        </p>
      </div>

      {/* SORT DESC */}
      <div
        className={styles.child}
        onClick={() => {
          setSort(2);
          hide();
          localStorage.setItem(filterId + 'sort', '2');
        }}
      >
        <AiOutlineArrowDown color={sort === 2 ? '#272944' : '#000'} size={20} />
        <p className={styles.text}>
          {column.Header === 'Lead Assign Date & Time'
            ? 'Latest to Oldest'
            : column.Header === 'Created Date & Time'
              ? 'Latest to Oldest'
              : 'Sort Z to A'}
        </p>
      </div>
      <div className={styles.line} />

      {/* ------------------------------------
         DATE FILTER MODE
      ------------------------------------ */}
      {dateFieldList.includes(column.Header) ? (
        <div className={styles.dateInputContainer}>
          {/* From */}
          <p
            className={styles.optionText}
            style={{ marginTop: '5px', marginBottom: '4px' }}
          >
            From:
          </p>
          <input
            type="date"
            id="from"
            name="from"
            className={styles.dateInput}
            value={
              dateArray[0] ? moment(dateArray[0]).format('YYYY-MM-DD') : ''
            }
            onChange={(e) => {
              setDateFilter(e.target.valueAsDate, 'from');
            }}
          />
          {/* To */}
          <p
            className={styles.optionText}
            style={{ marginTop: '12px', marginBottom: '4px' }}
          >
            To:
          </p>
          <input
            type="date"
            id="to"
            name="to"
            className={styles.dateInput}
            value={
              dateArray[1] ? moment(dateArray[1]).format('YYYY-MM-DD') : ''
            }
            onChange={(e) => setDateFilter(e.target.valueAsDate, 'to')}
          />
        </div>
      ) : (
        <>
          {/* Search */}
          <div className={styles.searchBox}>
            <input
              className={styles.searchText}
              placeholder={'Search'}
              onChange={(val) => setsearchedItem(val.target.value)}
              value={searchedItem}
            />
            {searchedItem.length === 0 ? (
              <AiOutlineSearch color={'#272944'} size={15} />
            ) : (
              <AiOutlineClose
                color={'#ff0000'}
                size={15}
                onClick={() => setsearchedItem('')}
              />
            )}
          </div>
          {/* Filter list */}
          <div className={styles.resultBox}>
            <InfiniteScroll
              height={150}
              dataLength={currentLimit}
              next={() => setCurrentLimit(currentLimit + 5)}
              hasMore={filterItems?.length > currentLimit}
              loader={<h4>Loading...</h4>}
              className={styles.infiniteScroll}
            >
              <>
                <div className={styles.checkView}>
                  <Checkbox
                    style={checkboxStyles}
                    size="small"
                    onChange={(e) => {
                      let data: any[] = [...filterItems];
                      if (e.target.checked) {
                        data = [...filterItems];
                        setFilterRow(data);

                        localStorage.setItem(filterId, JSON.stringify(data));
                      } else if (!e.target.checked) {
                        data = [];
                        setFilterRow(data);

                        localStorage.setItem(filterId, JSON.stringify(data));
                      }

                      dispatcher(setFilter({ [filter]: data }));
                    }}
                    checked={
                      filterRow &&
                      filterItems &&
                      filterRow.length === filterItems.length
                    }
                  />
                  <p className={styles.optionText}>Select All</p>
                </div>
                {/* VALUES LIST */}
                {filterItems &&
                  filterItems.slice(0, currentLimit).map((item, index) => (
                    <div key={index} className={styles.checkView}>
                      <Checkbox
                        style={checkboxStyles}
                        size="small"
                        onChange={(e) => {
                          const data = [...filterRow];
                          if (e.target.checked) {
                            data.push(item);
                            setFilterRow(data);
                          } else if (!e.target.checked) {
                            const val = data.indexOf(item);
                            if (val > -1) {
                              data.splice(val, 1);
                            }
                            setFilterRow(data);
                            column.setFilter(undefined);
                          }
                          localStorage.setItem(filterId, JSON.stringify(data));
                        }}
                        checked={filterRow.includes(item)}
                      />
                      <p className={styles.optionText}>{item}</p>
                    </div>
                  ))}
              </>
            </InfiniteScroll>
          </div>
        </>
      )}

      {/* Bottom buttons */}
      <div className={styles.buttonBox}>
        <button className={styles.clearButton} onClick={clearFilter}>
          Clear Filter
        </button>
        <button className={styles.applyButton} onClick={close}>
          Close
        </button>
      </div>
    </div>
  );
};


// -------------------------------------------------------------
// Redux: Mapping store state to props
// -------------------------------------------------------------
const mapStateToProps = (state: any) => {
  return {
    filterObject: state.filterObject,
    globalClearFilter: state.clearFilter.clear,
    theme: state.theme.isLightMode
  };
};

export default connect(mapStateToProps)(FilterModal);
